import React, { useEffect, useState, useMemo } from 'react';
import { Checkbox, Text, Loader, ActionIcon, Button } from '@mantine/core';
import { useForm, yupResolver } from '@mantine/form';
import InputField from 'src/components/common/Inputs/InputField';
import { CustomButton } from '../../common/Buttons/CustomButton';
import SelectField from '../../common/Inputs/SelectField';
import { getSchoolsByLevel, registerChildSchema } from './metadata';
import AvatarSelection from 'src/components/common/Inputs/AvatarInputField';
import useChildrenData from 'src/hooks/useChildrenData';
import { Subject } from 'src/services/services/parent/parent.api';
import Icon from 'src/components/common/Icons/Icons/Icon';

// Mapping of grade level names to typical ages
const AGE_OFFSET: { [key: string]: number } = {
  'Primary 3': 10,
  'Primary 4': 11,
  'Primary 5': 12,
  'Primary 6': 13,
  'Secondary 1': 14,
  'Secondary 2': 15,
  'Secondary 3': 16,
  'Secondary 4': 17,
};

const currentYear = new Date().getFullYear();

export interface RegisterFormData {
  image: File | null;
  firstName: string;
  lastName: string;
  birthYear: number;
  grade: string;
  // ChildLevel
  subjects: string[];
  school: string;
  username: string;
  password: string;
}

interface RegisterChildFormProps {
  onSubmit: (data: RegisterFormData, cb: () => void) => void;
  loading: boolean;
  error: string | null;
  setError: (error: string | null) => void;
}

const defaultUsername = 'john_doe';

const RegisterChildForm: React.FC<RegisterChildFormProps> = ({
  onSubmit,
  loading: formLoading,
  error,
  setError,
}) => {
  // Auto-dismiss error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, setError]);
  const [suggestedUsername, setSuggestedUsername] = useState(defaultUsername);
  const [isLoadingSubjects, setIsLoadingSubjects] = useState(false);
  const [selectedLevelName, setSelectedLevelName] = useState('');
  const {
    gradeLevels,
    subjects,
    loadingGradeLevels,
    loadingSubjects,
    fetchSubjectsByGrade,
  } = useChildrenData();

  // Get schools based on selected level
  const schoolOptions = useMemo(() => {
    return getSchoolsByLevel(selectedLevelName);
  }, [selectedLevelName]);

  const form = useForm<RegisterFormData>({
    initialValues: {
      image: null,
      firstName: '',
      lastName: '',
      birthYear: new Date().getFullYear(),
      grade: '',
      subjects: [],
      school: '',
      username: '',
      password: '',
    },
    validate: yupResolver(registerChildSchema),
    validateInputOnBlur: true,
    validateInputOnChange: true,
  });

  useEffect(() => {
    console.log('grade in form : ', form.values.grade);
  }, [form.values.grade]);

  // Format grade levels for the select component
  const gradeLevelOptions = useMemo(() => {
    console.log('grade levels : ', gradeLevels);
    if (gradeLevels && gradeLevels.length == 0) {
      return [];
    }
    return gradeLevels.map((level: { id: string; name: string }) => ({
      value: level.id,
      label: level.name,
    }));
  }, [gradeLevels]);

  // Automatically select the first grade level when gradeLevels are loaded
  useEffect(() => {
    if (gradeLevels?.length > 0 && !form.values.grade) {
      const firstLevel = gradeLevels[0];
      form.setFieldValue('grade', firstLevel.id);
      setSelectedLevelName(firstLevel.name);
      fetchSubjectsByGrade(firstLevel.id);
    }
  }, [gradeLevels]);

  // Format subjects for the select component and handle required subjects
  const subjectOptions = useMemo(() => {
    console.log('subjects : ', subjects);
    if (!subjects || subjects.length === 0) {
      // Clear subjects when no subjects are available
      form.setFieldValue('subjects', []);
      return [];
    }

    // Clear subjects when grade changes
    if (form.values.grade) {
      // Get required subjects for the current grade
      const requiredSubjects = subjects.filter(
        (subject: Subject) => subject.is_required
      );

      // Clear all subjects first
      form.setFieldValue('subjects', []);

      // Add required subjects if any
      if (requiredSubjects.length > 0) {
        const requiredSubjectIds = requiredSubjects.map((s: Subject) => s.id);
        form.setFieldValue('subjects', requiredSubjectIds);
      }
    }

    return subjects.map((subject: Subject) => ({
      value: subject.id,
      label: subject.name,
      is_required: subject.is_required,
    }));
  }, [subjects, form.values.grade]);

  // Handle grade level change
  const handleGradeLevelChange = async (value: string | null) => {
    form.setFieldValue('grade', value || '');
    form.setFieldValue('subjects', []);

    // Find the selected level name
    const selectedLevel = gradeLevels.find((level) => level.id === value);
    if (selectedLevel) {
      setSelectedLevelName(selectedLevel.name);
    }

    if (value) {
      setIsLoadingSubjects(true);
      try {
        await fetchSubjectsByGrade(value);
      } finally {
        setIsLoadingSubjects(false);
      }
    }
  };

  const generateSuggestedUsername = () => {
    const firstName = form.values.firstName.replace(/\s+/g, '');
    const lastName = form.values.lastName.replace(/\s+/g, '');
    setSuggestedUsername(
      `${firstName.toLowerCase()}_${lastName.toLowerCase()}`
    );
  };

  useEffect(() => {
    generateSuggestedUsername();
  }, [form.values.firstName, form.values.lastName]);

  const handleSubmit = (values: RegisterFormData) => {
    onSubmit(values, () => {
      form.reset();
    });
  };

  return (
    <form
      onSubmit={form.onSubmit(handleSubmit)}
      className='h-full max-h-[calc(100dvh-55px-36px-16p-16px)] md:max-h-[calc(100dvh-140px-36px-16p-16px-70px)] md:pt-2 p-0 w-[95%] tablet:w-[80%] small-laptop:w-[60%] large-laptop:w-[60%] xl:w-[40%]'
    >
      <div className='relative flex flex-col justify-center'>
        <div className='flex flex-col items-center justify-between  gap-1 mb-4'>
          <AvatarSelection
            onChange={(file) => form.setFieldValue('image', file)}
            error={form.errors.image as string | undefined}
          />
          <Text className='text-lg '>Select an Avatar</Text>
        </div>
        <div className='grid grid-cols-2 gap-4'>
          <InputField
            label='First Name'
            inputContainerClassName='h-9 tablet:h-10 small-laptop:h-10 large-laptop:h-12 xl:h-12'
            className='text-sm sm:text-base'
            placeholder='Enter first name'
            {...form.getInputProps('firstName')}
          />
          <InputField
            label='Last Name'
            inputContainerClassName='h-9 tablet:h-10 small-laptop:h-10 large-laptop:h-12 xl:h-12'
            className='text-sm sm:text-base '
            placeholder='Enter last name'
            {...form.getInputProps('lastName')}
          />
        </div>
        {/* </div> */}

        <div className='grid grid-cols-2 gap-4'>
          <SelectField
            selectClassName='h-9 tablet:h-10 small-laptop:h-10 large-laptop:h-12 xl:h-12 text-sm sm:text-base'
            label='Birth Year'
            placeholder='Select year of birth'
            data={(() => {
              const selectedGrade = gradeLevels.find(
                (level) => level.id === form.values.grade
              );
              const gradeName = selectedGrade?.name || '';
              const age = AGE_OFFSET[gradeName] || 12; // Default to 12 if grade not found

              // Generate an array of years: [age-1, age, age+1]
              return [1, 0, -1].map((offset) => ({
                value: (currentYear - age + offset).toString(),
                label: (currentYear - age + offset).toString(),
              }));
            })()}
            {...form.getInputProps('birthYear')}
          />
          {loadingGradeLevels ? (
            <div className='flex items-center justify-center p-4'>
              <Loader size='sm' />
              <Text className='ml-2'>Loading grade levels...</Text>
            </div>
          ) : (
            <SelectField
              label='Grade Level'
              selectClassName='h-9 tablet:h-10 small-laptop:h-10 large-laptop:h-12 xl:h-12 text-sm sm:text-base'
              placeholder='Select grade level'
              data={gradeLevelOptions}
              value={form.values.grade}
              onChange={(value) => handleGradeLevelChange(value)}
              error={form.errors.grade as string | undefined}
              required
              searchable={false}
            />
          )}
        </div>

        {/* Subjects Section */}
        <div className='mt-4'>
          <Text className='text-sm font-medium mb-2'>Subjects</Text>
          {isLoadingSubjects ? (
            <div className='grid grid-cols-2 gap-3'>
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className='flex items-center'>
                  <div className='h-4 w-4 rounded bg-gray-200 animate-pulse' />
                  <div className='h-4 w-32 bg-gray-200 rounded animate-pulse ml-2' />
                </div>
              ))}
            </div>
          ) : loadingSubjects ? (
            <div className='flex items-center p-4'>
              <Loader size='sm' />
              <Text className='ml-2'>Loading subjects...</Text>
            </div>
          ) : subjectOptions.length > 0 ? (
            <div className='grid grid-cols-2 gap-3'>
              {subjectOptions.map((subject) => {
                const isRequired = subject.is_required;
                const isChecked = form.values.subjects.includes(subject.value);

                return (
                  <div key={subject.value} className='flex items-center'>
                    <Checkbox
                      label={subject.label}
                      checked={isChecked}
                      onChange={(event) => {
                        if (isRequired) return; // Prevent unchecking required subjects

                        const isChecked = event.currentTarget.checked;
                        const currentSubjects = [...form.values.subjects];

                        if (isChecked) {
                          form.setFieldValue('subjects', [
                            ...currentSubjects,
                            subject.value,
                          ]);
                        } else {
                          form.setFieldValue(
                            'subjects',
                            currentSubjects.filter((s) => s !== subject.value)
                          );
                        }
                      }}
                      classNames={{
                        input: `cursor-pointer border-gray-300 rounded text-blue-600 focus:ring-blue-500 ${
                          isRequired ? 'opacity-60' : ''
                        }`,
                        label: `cursor-pointer text-sm ${
                          isRequired ? 'font-semibold' : ''
                        }`,
                        body: 'items-center',
                      }}
                      radius='sm'
                      size='sm'
                      disabled={!form.values.grade || isRequired}
                    />
                    {isRequired && (
                      <span className='ml-1 text-xs text-gray-500'>
                        (Required)
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <Text className='text-gray-500 text-sm'>
              {form.values.grade
                ? 'No subjects available for this grade'
                : 'Select a grade level to see available subjects'}
            </Text>
          )}
          {form.errors.subjects && (
            <Text size='xs' color='red' mt={2} className='text-xs'>
              {form.errors.subjects}
            </Text>
          )}
        </div>
        <SelectField
          label='School'
          selectClassName='text-sm sm:text-base my-4 h-9 tablet:h-10 small-laptop:h-10 large-laptop:h-12 xl:h-12'
          placeholder='Select school'
          data={schoolOptions}
          searchable
          {...form.getInputProps('school')}
          required
          error={form.errors.school as string | undefined}
        />
        <InputField
          label='Username'
          inputContainerClassName='h-9 tablet:h-10 small-laptop:h-10 large-laptop:h-12 xl:h-12'
          className='text-sm sm:text-base'
          placeholder='Enter username'
          {...form.getInputProps('username')}
        />
        <Text className='hidden sm:block' size='xs' color='gray' mt={-8} mb={8}>
          Please choose a User ID without any space. e.g.{' '}
          {suggestedUsername == '_' ? defaultUsername : suggestedUsername}
        </Text>
        <InputField
          label='Password'
          type='password'
          inputContainerClassName='h-9 tablet:h-10 small-laptop:h-10 large-laptop:h-12 xl:h-12'
          className='text-sm sm:text-base'
          placeholder='Enter password'
          {...form.getInputProps('password')}
          showPasswordToggle
        />
        <Text size='xs' color='gray' mt={-8}>
          Use at least one capital letter, one special character and a number in
          your password.
        </Text>
      </div>
      {/* {error && (
          <div className='mb-4 p-3 bg-red-50/80 border border-red-200 rounded-md absolute bottom-0 right-10 z-10'>
            <Text
              size='sm'
              color='red'
              className='text-center flex gap-2 items-center'
            >
              <ActionIcon onClick={() => setError(null)}>
                <Icon name='cross' size='1rem' color='red' />
              </ActionIcon>
              {error}
            </Text>
          </div>
        )} */}
      <CustomButton
        loading={formLoading}
        size='sm'
        type='submit'
        disabled={
          !form.isValid() ||
          loadingGradeLevels ||
          loadingSubjects ||
          (!form.values.grade && form.values.subjects.length > 0)
        }
        className='mt-4 mb-2 w-full static'
      >
        Register
      </CustomButton>
    </form>
  );
};

export default RegisterChildForm;
